import { FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useErrorBoundary, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Button, GraphQLErrorMessage, Heading3, Input, Label, tokens } from '@treams/treams-ui';

import Group from '../../goals/group-selector/Group';
import SingleGroupSelector from '../../goals/group-selector/SingleGroupSelector';

const Header = styled(Flex)`
	align-items: center;
	margin-bottom: ${tokens.gap.group}px;
	padding-bottom: ${tokens.gap.group}px;
	border-bottom: 1px solid ${tokens.border.subtle};
`;

export default function DepartmentDetails(props: { group: graphql.Group }) {
	const { t } = useTranslation();
	const [editing, setEditing] = useState(false);
	const { isHRBusinessPartner } = useViewerRoles();

	const [update] = graphql.useUpdateGroupMutation();
	const [move] = graphql.useMoveGroupMutation();

	const [error, attempt, reset] = useErrorBoundary();

	const formik = useFormik({
		initialValues: {
			name: props.group?.name ?? '',
			hris: props.group?.externalReferenceID ?? '',
			groupID: props.group?.parent?.id,
		},
		onSubmit: () => {},
	});

	useEffect(() => {
		formik.setValues({
			name: props.group?.name ?? '',
			hris: props.group?.externalReferenceID ?? '',
			groupID: props.group?.parent?.id,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!props.group, props.group?.id]);

	useEffect(() => {
		if (!props.group) {
			return;
		}

		reset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(formik.values)]);

	useEffect(() => {
		if (error && !editing && !isHRBusinessPartner) {
			setEditing(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!error, editing, isHRBusinessPartner]);

	return (
		<FormikProvider value={formik}>
			<Header>
				<Heading3 flexGrow={1} flexShrink={1} mb={0}>
					{t('settings.employees.personal.title')}
				</Heading3>
				<Box flexGrow={0} flexShrink={0}>
					{editing ? (
						<Button
							type="primary"
							title={t('common.save')}
							onClick={attempt(async () => {
								if (!props.group) {
									return;
								}

								setEditing(false);
								await update({
									variables: {
										input: {
											id: props.group.id,
											name: formik.values.name,
											externalReferenceID: formik.values.hris ? formik.values.hris : undefined,
										},
									},
								});

								if (props.group.parent?.id !== formik.values.groupID) {
									await move({
										variables: {
											input: {
												id: props.group.id,
												newParentID: formik.values.groupID,
											},
										},
									});
								}
							})}
						/>
					) : (
						!isHRBusinessPartner && (
							<Button
								type="secondary"
								disabled={!props.group.viewerCanUpdate}
								title={t('common.edit')}
								onClick={() => setEditing(true)}
							/>
						)
					)}
				</Box>
			</Header>
			{error && (
				<Box mb={2}>
					<GraphQLErrorMessage error={error} />
				</Box>
			)}
			<Box mb={2}>
				<Label mb={2}>{t('settings.departments.details.name')}:</Label>
				<Input.Formik key={`${editing}`} name="name" disabled={!editing} autoFocus={editing} />
			</Box>
			<Box mb={2}>
				<Label mb={2}>{t('settings.departments.details.hris')}:</Label>
				<Input.Formik key={`${editing}`} name="hris" disabled={!editing} autoFocus={editing} />
			</Box>
			<Box>
				<Label mb={2}>{t('settings.departments.details.parent')}:</Label>
				<SingleGroupSelector.Formik
					types={[graphql.GroupType.Department]}
					required
					disabled={!editing}
					name="groupID"
					canSelect={(group) => group.type === graphql.GroupType.Department}
				/>
			</Box>
		</FormikProvider>
	);
}

function DepartmentDetailsSkeleton() {
	const { t } = useTranslation();

	return (
		<>
			<Flex alignItems="center" mb={3}>
				<Heading3 flexGrow={1} flexShrink={1} mb={0}>
					{t('settings.department.details.title')}
				</Heading3>
				<Box flexGrow={0} flexShrink={0}>
					<Button.Skeleton />
				</Box>
			</Flex>
			<Box mb={2}>
				<Label mb={2}>{t('settings.departments.details.name')}:</Label>
				<Input.Skeleton />
			</Box>
			<Box mb={2}>
				<Label mb={2}>{t('settings.departments.details.hris')}:</Label>
				<Input.Skeleton />
			</Box>
			<Box>
				<Label mb={2}>{t('settings.departments.details.parent')}:</Label>
				<Group.Skeleton />
			</Box>
		</>
	);
}

DepartmentDetails.Skeleton = DepartmentDetailsSkeleton;
